import { useEffect, useMemo, useState } from "react";
import { getDrugDetails, getDrugPedsDosing, type DrugDetails, type DrugMolecule, type PedsDosing } from "@/lib/api";
import { hasUnsplittableSuffix } from "@/lib/constants";
import { parseDosagePattern } from "@/lib/dosage";
import {
    checkTypedDose, ingredientTotals, dailyCeiling, amountPerUnit, productKind, isSplittableForm,
    routeMatches, unitFamily, normUnit, type IngredientLoad, type Note, type Product,
} from "@/lib/pedsDose";

export type RowDoseCheck = {
    level: "ok" | "warning" | "error" | "none";
    summary: string;   // short text shown under the dosage cell
    notes: Note[];     // full detail (tooltip)
};

type MedicineRow = { drug_id?: number | null; dosage?: string | null };

// Shared across mounts: drug details and reference rows rarely change during a session
const detailsCache = new Map<number, Promise<DrugDetails | null>>();
const dosingCache  = new Map<string, Promise<PedsDosing[]>>();

function loadDetails(id: number) {
    if (!detailsCache.has(id)) {
        detailsCache.set(id, getDrugDetails(id).catch(() => null).then(d => {
            if (!d) detailsCache.delete(id);   // retry on the next render rather than caching a failure
            return d;
        }));
    }
    return detailsCache.get(id)!;
}

function loadDosing(drugId: number, moleculeId: number) {
    const key = `${drugId}|${moleculeId}`;
    if (!dosingCache.has(key)) {
        dosingCache.set(key, getDrugPedsDosing(drugId, moleculeId).then(r => r ?? []).catch(() => {
            dosingCache.delete(key);
            return [];
        }));
    }
    return dosingCache.get(key)!;
}

function productFor(details: DrugDetails, m: DrugMolecule, fallbackPerVolume: number | null): Product {
    const form = details.form ?? "";
    return {
        form,
        kind: productKind(form, m.strength_type),
        splittable: isSplittableForm(form) && !hasUnsplittableSuffix(details.brand_name),
        strength: {
            amount: m.dose_value ? Number(m.dose_value) : null,
            unit: normUnit(m.dose_unit) || "mg",
            perVolume: m.per_volume_value ? Number(m.per_volume_value) : fallbackPerVolume,
            perVolumeUnit: "ml",
        },
    };
}

const SEVERITY = { none: 0, ok: 1, warning: 2, error: 3 } as const;

/**
 * Passive dose check for every medicine row of a pediatric prescription: the typed dosage is
 * converted back to mg/kg and checked against the reference, and the same ingredient in several
 * rows is added up. Runs without the calculator being opened.
 */
export function usePrescriptionDoseChecks(
    medicines: MedicineRow[],
    weightKg: number,
    ageMonths: number | null,
    enabled: boolean,
): Map<number, RowDoseCheck> {
    const [details, setDetails] = useState<Record<number, DrugDetails | null>>({});
    const [dosing, setDosing]   = useState<Record<string, PedsDosing[]>>({});

    const drugIds = enabled
        ? [...new Set(medicines.map(m => m.drug_id).filter((id): id is number => typeof id === "number"))]
        : [];
    const idsKey = drugIds.sort((a, b) => a - b).join(",");

    useEffect(() => {
        if (!idsKey) return;
        let cancelled = false;
        for (const id of idsKey.split(",").map(Number)) {
            if (id in details) continue;
            loadDetails(id).then(async d => {
                if (cancelled) return;
                setDetails(prev => ({ ...prev, [id]: d }));
                if (!d) return;
                for (const m of d.molecules ?? []) {
                    const rows = await loadDosing(id, m.molecule_id);
                    if (cancelled) return;
                    setDosing(prev => ({ ...prev, [`${id}|${m.molecule_id}`]: rows }));
                }
            });
        }
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    const dosageKey = medicines.map(m => `${m.drug_id ?? ""}:${m.dosage ?? ""}`).join("|");

    return useMemo(() => {
        const result = new Map<number, RowDoseCheck>();
        if (!enabled) return result;
        const wt = weightKg > 0 ? weightKg : null;
        const loads: IngredientLoad[] = [];

        medicines.forEach((med, index) => {
            const d = typeof med.drug_id === "number" ? details[med.drug_id] : null;
            const parsed = parseDosagePattern(med.dosage);
            if (!d || !parsed) return;
            const molecules = d.molecules ?? [];
            const primary = molecules.find(m => m.is_primary) ?? (molecules.length === 1 ? molecules[0] : undefined);
            const primaryPerVolume = primary?.per_volume_value ? Number(primary.per_volume_value) : null;
            const rowsFor = (m: DrugMolecule) => (dosing[`${d.id}|${m.molecule_id}`] ?? []).filter(r => routeMatches(r.route, d.route));

            // Per-kg check on the primary ingredient (skipped for combinations with no primary — ambiguous)
            if (primary) {
                const check = checkTypedDose({
                    parsed, product: productFor(d, primary, null), weightKg, ageMonths,
                    moleculeName: primary.molecule_name, rows: rowsFor(primary),
                });
                if (check) result.set(index, { level: check.level, summary: check.summary, notes: check.notes });
            }

            // Every ingredient's daily amount, for the cross-medicine totals
            for (const m of molecules) {
                const product = productFor(d, m, primaryPerVolume);
                const perUnit = amountPerUnit(product, parsed.unit);
                const family = unitFamily(product.strength.unit);
                if (perUnit == null || !family) continue;
                loads.push({
                    rowIndex: index, moleculeId: m.molecule_id, name: m.molecule_name,
                    daily: parsed.perDay * perUnit, family,
                    ceiling: dailyCeiling(rowsFor(m), wt, ageMonths),
                });
            }
        });

        for (const [index, extra] of ingredientTotals(loads)) {
            const existing = result.get(index);
            const level = extra.some(n => n.level === "error") ? "error" : "warning";
            if (!existing) {
                result.set(index, { level, summary: extra[0].text.split(" — ")[0], notes: extra });
                continue;
            }
            const worse = SEVERITY[level] > SEVERITY[existing.level];
            result.set(index, {
                level: worse ? level : existing.level,
                // A cross-medicine overdose outranks the row's own status in the one-line summary
                summary: worse && level === "error" ? `${extra[0].text.split(" — ")[0]} · over max/day` : existing.summary,
                notes: [...existing.notes, ...extra],
            });
        }
        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, dosageKey, details, dosing, weightKg, ageMonths]);
}
